'use client';

import { algoliasearch } from 'algoliasearch';

import { recommendClient as createRecommendClient } from '@algolia/recommend';

import { getAlgoliaFailoverStatus, markAlgoliaAsDown } from './algoliaFailover';
import { ALGOLIA_API_KEY, ALGOLIA_APPLICATION_ID } from './env';

const originalAlgoliaClient = algoliasearch(ALGOLIA_APPLICATION_ID!, ALGOLIA_API_KEY!);
const originalRecommendClient = createRecommendClient(ALGOLIA_APPLICATION_ID!, ALGOLIA_API_KEY!);

const isAlgoliaServiceError = (error: any): boolean => {
  // Check for timeout errors
  if (error.name === 'TimeoutError' || error.message?.includes('timeout')) {
    return true;
  }

  // Check for 5xx server errors (service unavailable, internal server error, etc.)
  if (error.status >= 500 && error.status < 600) {
    return true;
  }

  // Check for specific Algolia service error messages
  const serviceErrorMessages = [
    'service unavailable',
    'internal server error',
    'bad gateway',
    'gateway timeout',
    'service temporarily unavailable',
    'algolia service',
    'cluster',
    'maintenance',
  ];

  const errorMessage = error.message?.toLowerCase() || '';
  return serviceErrorMessages.some(msg => errorMessage.includes(msg));
};

const createFailoverWrapper = <T extends any[], R>(originalFunction: (...args: T) => Promise<R>) => {
  // eslint-disable-next-line sonarjs/cognitive-complexity
  return async (...args: T): Promise<R> => {
    // DEVELOPMENT CODE - COMMENTED OUT FOR PRODUCTION
    // if (process.env.NEXT_PUBLIC_ALGOLIA_USE_TEST_API === 'true') {
    //   try {
    //     const response = await fetch('/api/test-algolia-health-check');
    //     const isHealthy = response.ok;

    //     if (isHealthy) {
    //       return await originalFunction(...args);
    //     } else {
    //       if (!getAlgoliaFailoverStatus()) {
    //         console.log('Test API returned 500 - marking Algolia as down');
    //         markAlgoliaAsDown();
    //       }
    //       throw new Error('Algolia service is temporarily unavailable. Please try again later.');
    //     }
    //   } catch (error: any) {
    //     if (!getAlgoliaFailoverStatus()) {
    //       console.log('Test API call failed - marking Algolia as down');
    //       markAlgoliaAsDown();
    //     }
    //     throw new Error('Algolia service is temporarily unavailable. Please try again later.');
    //   }
    // }

    try {
      return await originalFunction(...args);
    } catch (error: any) {
      // Don't trigger failover for cancellations
      if (error.name === 'AbortError' || error.message?.includes('cancelled')) {
        throw error;
      }

      // Only trigger failover for service-related errors
      if (isAlgoliaServiceError(error) && !getAlgoliaFailoverStatus()) {
        console.log('Algolia service error detected, marking as down:', error.message);
        markAlgoliaAsDown();
      } else {
        console.log('Algolia API error (not service-related):', error.message);
      }

      throw error;
    }
  };
};

export const algoliaClient = {
  ...originalAlgoliaClient,
  searchSingleIndex: createFailoverWrapper(originalAlgoliaClient.searchSingleIndex),
  search: createFailoverWrapper(originalAlgoliaClient.search),
  initAnalytics: originalAlgoliaClient.initAnalytics,
};

export const recommendClient = {
  ...originalRecommendClient,
  getRecommendations: createFailoverWrapper(originalRecommendClient.getRecommendations),
};

export const originalClient = originalAlgoliaClient;
export const originalRecommendClientExport = originalRecommendClient;
